/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useRecordingManagementList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing Recording Management list data and DataGrid state.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchRecordings } from '../api';
import { RecordingFilters } from '../types';
import { useRecordingManagementColumns } from '../components/RecordingManagementColumns';

interface UseRecordingManagementListProps {
  filters?: RecordingFilters;
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Custom React hook for managing the Recording Management DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * Supports Domain Contextual Filtering to only show recordings belonging to the current tenant.
 * 
 * @param props - Configuration properties for the list hook.
 * @returns Combined DataGrid state and the effective Domain ID applied to the filter.
 */
export function useRecordingManagementList(props: UseRecordingManagementListProps = {}) {
  const initialColumns = useRecordingManagementColumns();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    const result = await fetchRecordings({
      ...params,
      ...props.filters,
      domainId: effectiveDomainId,
    });
    return {
      data: result.data,
      pagination: result.pagination,
    };
  }, [props.filters, effectiveDomainId]);

  const dataGridResult = useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    moduleName: 'recordingManagement',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...dataGridResult,
    effectiveDomainId,
  };
}

